<template>
  <app-page
    :title="`Update ${isCompletingUnenrolment ? 'Terminated' : 'Opt Out'} Employee Bank Details`"
    :error="error"
    :previous="[
      { label: 'Employees', to: { name: 'employees' } },
      {
        label: employee ? employee.full_name : 'Employee',
        to: { name: 'employee', params: { employeeId: $route.params.employeeId } },
      },
    ]"
  >
    <app-loading :loading="loading" />
    <template v-if="!loading && !success">
      <sme-alert level="warning" class="mb-3">
        <strong>
          Please update your payroll details with the employee's current {{ getBankingInfo.sortCode.label }} and
          {{ getBankingInfo.accountNumber.label }}.
        </strong>
        <br />
        These will replace the details of their Stream account that are currently set up in your system.
      </sme-alert>

      <app-input
        v-model="employee.full_name"
        type="text"
        name="employee name"
        label="Employee Name"
        readonly
      ></app-input>

      <app-input
        v-model="employee.employee_code"
        type="text"
        name="employee code"
        label="Employee Code"
        readonly
      ></app-input>

      <app-input
        v-model="employee.properties.beneficiary_sort_code"
        type="text"
        name="sort code"
        readonly
        :label="getBankingInfo.sortCode.label"
      ></app-input>

      <app-input
        v-model="employee.properties.beneficiary_account_number"
        type="text"
        name="account number"
        readonly
        :label="getBankingInfo.accountNumber.label"
      ></app-input>

      <ValidationObserver v-slot="v">
        <b-form @submit.prevent="v.handleSubmit(onSubmit)" id="updated">
          <ValidationProvider v-slot="f" name="updated" :rules="{ required: { allowFalse: false } }">
            <b-form-checkbox v-model="updated">I have updated this employee in my company payroll file</b-form-checkbox>
            <b-form-invalid-feedback v-for="fError in f.errors" :key="fError">{{ fError }}</b-form-invalid-feedback>
          </ValidationProvider>

          <page-sub-footer>
            <b-button
              data-testid="revertbanking-button"
              type="submit"
              variant="primary"
              :disabled="v.invalid || saving || success"
            >
              <b-spinner v-if="saving" class="mr-2" small />
              {{ submitLabel }}
            </b-button>
          </page-sub-footer>
        </b-form>
      </ValidationObserver>
    </template>
    <sme-alert v-if="!loading && success" level="good">{{ successMessage }}</sme-alert>
  </app-page>
</template>

<script>
import * as moment from 'moment/moment';

import { ValidationObserver, ValidationProvider } from 'vee-validate';
import ApiClient from '@/ApiClient';
import { getBankingInfo } from '@/Banking';
import AppInput from '@/components/AppInput.vue';
import AppLoading from '@/components/AppLoading.vue';
import AppPage from '@/components/AppPage.vue';
import PageSubFooter from '@/components/PageSubFooter.vue';
import SmeAlert from '@/components/atoms/SmeAlert.vue';
import State from '@/state/State';

export default {
  name: 'EmployeeBankingSetup',
  components: { ValidationObserver, ValidationProvider, AppInput, AppLoading, AppPage, PageSubFooter, SmeAlert },
  data() {
    return {
      loading: true,
      error: undefined,
      saving: false,
      success: false,
      registration: undefined,
      employee: undefined,
      state: State.state,
      updated: undefined,
    };
  },
  methods: {
    async onSubmit() {
      this.saving = true;

      const newEmployee = {
        ...this.employee,
        properties: {
          ...this.employee.properties,
          optout_banking_updated: moment.utc().format(),
        },
      };

      try {
        await ApiClient.updateEmployee(newEmployee);

        // Opt-out and unenrolment are completed by different endpoints, and an employee part-way
        // through both would otherwise race two requests against each other. Opt-out wins because
        // it is the one that blocks the employee's access.
        if (this.employee.properties.optout_initiated_at) {
          await ApiClient.employeeOptOutComplete(this.employee.employee_id);
        } else if (this.employee.properties.unenrolment_initiated_at) {
          await ApiClient.employeeUnenrolmentComplete(this.employee.employee_id);
        } else {
          // Nothing to complete; without this the button would spin forever with no explanation.
          this.error = 'This employee has no outstanding opt-out or termination to complete.';
          this.saving = false;
          return;
        }

        this.success = true;
      } catch (error) {
        this.error = error.message;
      }

      this.saving = false;
      // TODO: refresh global TODOs
    },
    loadData() {
      this.loading = true;
      ApiClient.getEmployee(this.$route.params.employeeId).then(
        employee => {
          this.employee = employee;
          this.loading = false;
        },
        error => {
          this.error = error.message;
          this.loading = false;
        },
      );
    },
  },
  mounted() {
    this.loadData();
  },
  computed: {
    getBankingInfo: () => getBankingInfo(),
    // The same page completes an opt-out and a termination; an opt-out that is still
    // outstanding takes priority, matching the order onSubmit completes them in.
    isCompletingUnenrolment() {
      return Boolean(
        this.employee &&
          !this.employee.properties?.optout_initiated_at &&
          this.employee.properties?.unenrolment_initiated_at,
      );
    },
    submitLabel() {
      if (this.isCompletingUnenrolment) {
        return this.saving ? 'Completing...' : 'Complete termination';
      }

      return this.saving ? 'Opting out...' : 'Opt out';
    },
    successMessage() {
      return this.isCompletingUnenrolment
        ? 'Employee termination has been completed'
        : 'Employee opt-out has been completed';
    },
  },
};
</script>

<style>
#updated .invalid-feedback {
  display: block;
}
</style>
